/**
 * Remove once migrate into field-rep-frontend.
 */
import { type ReactNode, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { GetCurrentUserResponse } from "@labeling-tools/api/field-rep/generated/types.gen";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isAccountMissing } from "@/utils/api";
import { logout, useAuthUser } from "@/utils/firebase";
import { loginPath } from "@/utils/redirect";
import { AuthCard } from "./AuthCard";

// Every track labeling task belongs to this company).
const PRODUCT_LABELING_COMPANY_SLUG = "product-labeling";

const canLabel = (account: GetCurrentUserResponse): boolean => {
  if (account.is_staff) {
    return true;
  }

  const editorCompanies = account.permissions["verification.editor"] ?? [];
  return editorCompanies.some(
    (company) => company.slug === PRODUCT_LABELING_COMPANY_SLUG,
  );
};

/** Checks the logged in user's field-rep account before showing the app. */
const AccountCheck = ({
  email,
  children,
}: {
  email: string;
  children: ReactNode;
}) => {
  const { data: account, error } = useCurrentUser();

  if (error) {
    const message = isAccountMissing(error)
      ? `No account was found for ${email}. Accept your invitation first.`
      : `Couldn't check your account: ${error.message}`;
    return <UnauthorizedCard>{message}</UnauthorizedCard>;
  }

  if (!account) {
    return null;
  }

  if (!canLabel(account)) {
    return (
      <UnauthorizedCard>
        {email} can't label tasks yet. Ask field-rep team to give you the
        reviewer role for Product Labeling.
      </UnauthorizedCard>
    );
  }

  return children;
};

const UnauthorizedCard = ({ children }: { children: ReactNode }) => (
  <AuthCard title="Can't open Track Labeling">
    <p className="text-sm text-[#475569]">{children}</p>
    <button
      type="button"
      className="text-sm font-medium text-[#7C3AED] hover:underline"
      onClick={() => void logout()}
    >
      Use a different account
    </button>
  </AuthCard>
);

/**
 * Renders the app only for a logged in user who can label.
 */
export const LabelerGate = ({ children }: { children: ReactNode }) => {
  const authUser = useAuthUser();
  const location = useLocation();

  const [hasLoggedIn, setHasLoggedIn] = useState(false);
  if (authUser && !hasLoggedIn) {
    setHasLoggedIn(true);
  }

  if (authUser === undefined) {
    return null;
  }

  if (authUser === null) {
    const destination = hasLoggedIn
      ? "/login"
      : loginPath(location.pathname + location.search);
    return <Navigate to={destination} replace />;
  }

  return <AccountCheck email={authUser.email ?? ""}>{children}</AccountCheck>;
};
